/**
 * NumericStepper — atomic minus / value / plus stepper.
 *
 * Used by the Sell Hub bath-breakdown control (Full / 3/4 / Half), and
 * reusable elsewhere (filters, etc).
 *
 * Distinct from `ui/Stepper.js` which is the wizard progress strip — naming
 * chosen to avoid collision per design spec §6.3.
 *
 * Props:
 *  - id: string (required) — used to wire ARIA labels / output
 *  - label: string (required) — visible title
 *  - description: string — optional micro-spec ("toilet · sink · tub or shower")
 *  - value: number (required) — current count
 *  - min: number (default 0)
 *  - max: number (default 20)
 *  - step: number (default 1)
 *  - onChange: (newValue: number) => void
 *  - srLabel: string — accessible label override for the readout (defaults
 *      to `label`). Used when `label` is e.g. "Full baths" but the readout
 *      should announce "Full baths: 3".
 *  - testId: string — optional data-testid prefix; falls back to id.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import './NumericStepper.css';

const REPEAT_INITIAL_DELAY_MS = 400;
const REPEAT_INTERVAL_MS = 100;

export default function NumericStepper({
  id,
  label,
  description,
  value,
  min = 0,
  max = 20,
  step = 1,
  onChange,
  srLabel,
  testId,
}) {
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const repeatTimerRef = useRef(null);
  const repeatIntervalRef = useRef(null);
  const suppressClickRef = useRef(false);
  const announceLabel = srLabel || label;
  const tid = testId || id;

  onChangeRef.current = onChange;

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const clamp = useCallback(
    (n) => Math.max(min, Math.min(max, n)),
    [min, max],
  );

  const setValue = useCallback(
    (next) => {
      const clamped = clamp(next);
      if (clamped !== valueRef.current) onChangeRef.current(clamped);
    },
    [clamp],
  );

  const decrement = useCallback(() => setValue(valueRef.current - step), [setValue, step]);
  const increment = useCallback(() => setValue(valueRef.current + step), [setValue, step]);

  const stopRepeat = useCallback(() => {
    if (repeatTimerRef.current) {
      clearTimeout(repeatTimerRef.current);
      repeatTimerRef.current = null;
    }
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current);
      repeatIntervalRef.current = null;
    }
  }, []);

  // Cleanup any pending auto-repeat on unmount.
  useEffect(() => stopRepeat, [stopRepeat]);

  const startRepeat = useCallback(
    (op) => {
      // op is `decrement` or `increment`; run once immediately on press, then
      // auto-repeat after the initial delay (mirrors native scroll buttons).
      stopRepeat();
      op();
      suppressClickRef.current = true;
      repeatTimerRef.current = setTimeout(() => {
        repeatIntervalRef.current = setInterval(op, REPEAT_INTERVAL_MS);
      }, REPEAT_INITIAL_DELAY_MS);
    },
    [stopRepeat],
  );

  const runClick = useCallback((op) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    op();
  }, []);

  // Spinbutton keyboard nav on the readout: arrow keys adjust the value.
  const handleReadoutKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
      e.preventDefault();
      decrement();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
      e.preventDefault();
      increment();
    } else if (e.key === 'Home') {
      e.preventDefault();
      setValue(min);
    } else if (e.key === 'End') {
      e.preventDefault();
      setValue(max);
    }
  };

  const minusDisabled = value <= min;
  const plusDisabled = value >= max;

  return (
    <div
      className="numeric-stepper"
      role="group"
      aria-labelledby={`${id}-label`}
      aria-live="polite"
      data-testid={`${tid}-stepper`}
    >
      <div className="numeric-stepper__head">
        <span className="numeric-stepper__label" id={`${id}-label`}>{label}</span>
        {description && (
          <span className="numeric-stepper__desc" id={`${id}-desc`}>{description}</span>
        )}
      </div>
      <div className="numeric-stepper__controls">
        <button
          type="button"
          className="numeric-stepper__btn"
          onClick={() => runClick(decrement)}
          onMouseDown={() => startRepeat(decrement)}
          onMouseUp={stopRepeat}
          onMouseLeave={stopRepeat}
          onTouchStart={() => startRepeat(decrement)}
          onTouchEnd={stopRepeat}
          onTouchCancel={stopRepeat}
          aria-label={`Decrease ${announceLabel.toLowerCase()}`}
          aria-describedby={description ? `${id}-desc` : undefined}
          disabled={minusDisabled}
          data-testid={`${tid}-minus`}
        >
          <span aria-hidden="true">−</span>
        </button>
        <output
          className="numeric-stepper__value"
          role="spinbutton"
          tabIndex={0}
          aria-label={announceLabel}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          onKeyDown={handleReadoutKeyDown}
          data-testid={`${tid}-value`}
        >
          {value}
        </output>
        <button
          type="button"
          className="numeric-stepper__btn"
          onClick={() => runClick(increment)}
          onMouseDown={() => startRepeat(increment)}
          onMouseUp={stopRepeat}
          onMouseLeave={stopRepeat}
          onTouchStart={() => startRepeat(increment)}
          onTouchEnd={stopRepeat}
          onTouchCancel={stopRepeat}
          aria-label={`Increase ${announceLabel.toLowerCase()}`}
          aria-describedby={description ? `${id}-desc` : undefined}
          disabled={plusDisabled}
          data-testid={`${tid}-plus`}
        >
          <span aria-hidden="true">+</span>
        </button>
      </div>
    </div>
  );
}
